import { Form } from '@calendly/form';
import { useQueryParams } from '@calendly/hooks';
import { Recaptcha } from '@calendly/recaptcha';
import { Grid, GridItem } from '@calendly/ui/components/grid';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DirtyFormTracking } from 'components/core/form';
import { formSelector, modalSuccessSelector, roleSelector } from 'features/invite_flow/selectors';
import { setRecaptchaCallback, validateAndCreateFromModal } from 'features/invite_flow/slice';
import { buildInvitation } from 'features/invite_flow/utils';
import { getValidationSchema } from 'features/invite_flow/validation_schema';
import { MfaModal } from '../../mfa_modal';
import { useNewAdminModal } from '../use-new-admin-modal';
import { EmailsField } from './emails_field';
import { RoleSelect } from './role_select';
import { SubmitButton } from './submit_button';
const formWrapperCss = "ftitsn7";
const FormWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(formWrapperCss, props.className)} />;
});
const buttonWrapperIsPhoneCss = "bjpvkw0";
const buttonWrapperIsNewAdminModalCss = "bvxgo3a";
const buttonWrapperCss = "b1iesicj";
const ButtonWrapper = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  const isNewAdminModal = useNewAdminModal();
  return <GridItem ref={ref} {...props} className={classNames(buttonWrapperCss, {
    [buttonWrapperIsPhoneCss]: isPhone,
    [buttonWrapperIsNewAdminModalCss]: isNewAdminModal
  }, props.className)} />;
});
const roleWrapperCss = "r1ewt7e0";
const RoleWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(roleWrapperCss, props.className)} />;
});
export const InviteForm = ({
  availableSeats,
  onClose,
  inlineRoleSelect,
  viaModal = false,
  onSuccess = () => {}
}) => {
  const isPhone = useDeviceType('isPhone');
  const formData = useSelector(formSelector);
  const dispatch = useDispatch();
  const role = useSelector(roleSelector);
  const {
    email
  } = useQueryParams();
  const submitSuccess = useSelector(modalSuccessSelector);
  const [noEmailsError, setNoEmailsError] = useState(false);
  const isAdminFlagEnabled = useNewAdminModal();
  const initialInvitations = useMemo(() => formData.invitations.map(({
    email: e
  }) => e).includes(email) ? formData.invitations : [...formData.invitations, buildInvitation(email, null)], [formData.invitations, email]);
  const initialValues = useMemo(() => ({
    eventTypeIds: formData.eventTypeIds,
    invitations: initialInvitations,
    groupId: formData.groupId,
    modal: viaModal
  }), [formData.eventTypeIds, formData.groupId, initialInvitations, viaModal]);
  const create = useCallback(({
    eventTypeIds,
    invitations,
    groupId,
    verificationCode = null
  }) => {
    const updatedInvitations = invitations.map(invitation => ({
      id: invitation.id,
      email: invitation.email,
      role,
      userGroupId: invitation.userGroupId,
      userGroupRole: invitation.userGroupRole
    }));
    return dispatch(validateAndCreateFromModal({
      invitations: updatedInvitations,
      eventTypeIds,
      groupId,
      verificationCode
    }));
  }, [dispatch, role]);
  const onSubmit = useCallback(({
    eventTypeIds,
    invitations,
    groupId
  }) => {
    if (invitations.length === 0) {
      setNoEmailsError(true);
      return undefined;
    }
    return create({
      eventTypeIds,
      invitations,
      groupId
    });
  }, [create]);
  useEffect(() => {
    if (submitSuccess) {
      onSuccess();
      onClose();
    }
  }, [submitSuccess, onClose, onSuccess]);
  const recaptchaCallbackHandler = useCallback(callback => {
    dispatch(setRecaptchaCallback(callback));
  }, [dispatch]);
  const onChange = useCallback(() => {
    setNoEmailsError(false);
  }, []);
  return <FormWrapper>
      <Form id="add-users-form" initialValues={initialValues} validationSchema={getValidationSchema()} onSubmit={onSubmit} validateOnChange>
        <MfaModal onConfirm={create} />
        <DirtyFormTracking />
        <Grid>
          <GridItem size={isPhone ? 12 : 9}>
            <EmailsField maxSeats={availableSeats} showNoEmailsError={noEmailsError} onChange={onChange} inlineRoleSelect={inlineRoleSelect} />
          </GridItem>
          <ButtonWrapper size={isPhone ? 12 : 3}>
            {!isAdminFlagEnabled && <SubmitButton disabled={availableSeats === 0} />}
          </ButtonWrapper>
        </Grid>
        {!isAdminFlagEnabled && <RoleWrapper>
            <RoleSelect disabled={availableSeats === 0} />
          </RoleWrapper>}
        <Recaptcha action="organization/invitation" setVerifyCallback={recaptchaCallbackHandler} />
      </Form>
    </FormWrapper>;
};

require("./invite_form.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./invite_form.js");